import React, { useState, useEffect, useRef } from 'react';
import { Tag, Check, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config';

const PromoCodeInput = ({ bookingAmount, propertyId, userEmail, onCodeApplied }) => {
  const { toast } = useToast();
  const [code, setCode] = useState('');
  const [appliedCode, setAppliedCode] = useState(null);
  const [validating, setValidating] = useState(false);
  // Tracks whether we've already auto-applied an inbound code so we don't
  // re-trigger validation when the parent re-renders (e.g. bookingAmount
  // changes after the guest picks dates).
  const autoAppliedRef = useRef(false);

  const validateCode = async (codeOverride) => {
    // Defensive: callsites may accidentally pass a React event (e.g.,
    // `onClick={validateCode}`). Only accept actual string overrides —
    // otherwise fall back to the controlled `code` state.
    const override = typeof codeOverride === 'string' ? codeOverride : undefined;
    const effectiveCode = (override ?? code).trim();
    if (!effectiveCode) {
      toast({
        title: "Enter a code",
        description: "Please enter a promo code",
        variant: "destructive"
      });
      return;
    }

    setValidating(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/promo-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: effectiveCode,
          booking_amount: bookingAmount,
          property_id: propertyId,
          user_email: userEmail
        })
      });

      const data = await response.json();

      if (data.valid) {
        setAppliedCode(data);
        toast({
          title: `✅ Code Applied!`,
          description: `You saved $${data.savings.toFixed(2)}`,
          duration: 5000
        });
        
        if (onCodeApplied) {
          onCodeApplied(data);
        }
      } else {
        toast({
          title: "Invalid Code",
          description: data.error || "This promo code is not valid",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Promo code validation error:', error);
      toast({
        title: "Validation Failed",
        description: "Could not validate promo code",
        variant: "destructive"
      });
    } finally {
      setValidating(false);
    }
  };

  const removeCode = () => {
    setAppliedCode(null);
    setCode('');
    // Clear the WiFi-page-originated promo so it doesn't re-apply on the
    // next render after the guest explicitly removed it.
    try { sessionStorage.removeItem('hbl_inbound_promo'); } catch { /* ignore */ }

    if (onCodeApplied) {
      onCodeApplied(null);
    }
    
    toast({
      title: "Code Removed",
      duration: 2000
    });
  };

  // Auto-apply a code passed in via `?promo=CODE` (or persisted from a
  // previous page load) the first time `bookingAmount` is positive. We
  // wait for a real amount so the validator's min-booking check works.
  useEffect(() => {
    if (autoAppliedRef.current || appliedCode || validating) return;
    if (!bookingAmount || bookingAmount <= 0) return;

    let inbound = '';
    try {
      const params = new URLSearchParams(window.location.search);
      inbound = params.get('promo') || sessionStorage.getItem('hbl_inbound_promo') || '';
      if (params.get('promo')) {
        // Persist so it survives navigation to /property/... etc.
        sessionStorage.setItem('hbl_inbound_promo', params.get('promo'));
      }
    } catch { /* SSR / sandboxed iframes */ }

    if (!inbound) return;
    autoAppliedRef.current = true;
    const upper = inbound.toUpperCase();
    setCode(upper);
    validateCode(upper);
    // validateCode is stable enough here — including it would re-trigger on
    // its own ref bumps. eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingAmount]);

  // If code is applied, show summary
  if (appliedCode) {
    return (
      <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
              <Check className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-green-700" />
                <span className="font-bold text-green-900">
                  {appliedCode.promo_code}
                </span>
              </div>
              <p className="text-sm text-green-700 mt-1">
                {appliedCode.type === 'percentage' 
                  ? `${appliedCode.value}% off` 
                  : `$${appliedCode.value} off`}
              </p>
            </div>
          </div>

          <div className="text-right">
            <p className="text-sm text-green-600 font-semibold">
              You save ${appliedCode.savings.toFixed(2)}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={removeCode}
              className="text-green-700 hover:text-green-900 mt-1"
            >
              <X className="w-4 h-4 mr-1" />
              Remove
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Input form
  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-gray-700">
        Have a promo code?
      </label>
      
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyPress={(e) => e.key === 'Enter' && validateCode()}
            placeholder="Enter code"
            className="pl-10 uppercase"
            disabled={validating}
          />
        </div>
        
        <Button
          onClick={() => validateCode()}
          disabled={validating || !code.trim()}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {validating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Checking...
            </>
          ) : (
            'Apply'
          )}
        </Button>
      </div>

      <p className="text-xs text-gray-500">
        Enter your promo code to get a discount on your booking
      </p>
    </div>
  );
};

export default PromoCodeInput;
